import { Box, Loader, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChartTreatmentPlans } from '@/hooks/useChartEpisodeTreatmentPlan';
import type { TreatmentPlan } from '@/pages/clients/data/types';
import { ClinicalPanel } from '../ClinicalPanel';
import { useZoomSessionData } from '../hooks/useZoomSessionData';
import { useSessionPatientTreatmentPlan } from '../hooks/useSessionPatientTreatmentPlan';
import type { TreatmentPlanParticipantOption } from './TreatmentPlanClientSelect';

interface SessionClinicalPanelContentProps {
  appointmentId: string | undefined;
  layout?: 'overlay' | 'modal';
  onClose?: () => void;
  onRegisterFlush?: (flush: (() => Promise<void>) | null) => void;
  includeRecordingTab?: boolean;
  recordingTabContent?: React.ReactNode;
}

/**
 * Clinical sidebar used during live sessions (Zoom overlay and in-person session tools modal).
 */
export function SessionClinicalPanelContent({
  appointmentId,
  layout = 'overlay',
  onClose,
  onRegisterFlush,
  includeRecordingTab = false,
  recordingTabContent,
}: SessionClinicalPanelContentProps): JSX.Element {
  const session = useZoomSessionData(appointmentId);
  const panelFlushRef = useRef<(() => Promise<void>) | null>(null);
  const [
    selectedTreatmentPlanPatientId,
    setSelectedTreatmentPlanPatientId,
  ] = useState<string | undefined>();

  const flushSidebarNotes = session.flushSidebarNotes;

  const participantOptions = useMemo((): TreatmentPlanParticipantOption[] => {
    return session.contacts
      .filter((contact) => Boolean(contact.patientId))
      .map((contact) => ({
        patientId: contact.patientId,
        name: contact.name,
        initials: contact.initials,
      }));
  }, [session.contacts]);

  useEffect(() => {
    if (!session.isGroupTherapyEpisode) return;

    const patientIds = participantOptions.map((option) => option.patientId);
    if (patientIds.length === 0) {
      setSelectedTreatmentPlanPatientId(undefined);
      return;
    }

    if (
      !selectedTreatmentPlanPatientId ||
      !patientIds.includes(selectedTreatmentPlanPatientId)
    ) {
      setSelectedTreatmentPlanPatientId(patientIds[0]);
    }
  }, [
    session.isGroupTherapyEpisode,
    participantOptions,
    selectedTreatmentPlanPatientId,
  ]);

  const { entries: chartPlanEntries, loading: chartPlansLoading } = useChartTreatmentPlans(
    session.isGroupTherapyEpisode ? undefined : session.patientId,
    session.episodes,
    session.chartNotes,
    session.episodeId,
    'primary',
  );

  const groupTreatmentPlan = useSessionPatientTreatmentPlan({
    appointment: session.fhirAppointment,
    patientId: selectedTreatmentPlanPatientId,
    groupId: session.groupId,
    enabled: session.isGroupTherapyEpisode,
  });

  const plans = useMemo((): TreatmentPlan[] => {
    if (session.isGroupTherapyEpisode) {
      return groupTreatmentPlan.plans;
    }
    const fromChart = chartPlanEntries
      .map((entry) => entry.plan)
      .filter((plan): plan is TreatmentPlan => Boolean(plan));
    return fromChart;
  }, [
    session.isGroupTherapyEpisode,
    groupTreatmentPlan.plans,
    chartPlanEntries,
  ]);

  const plansLoading = session.isGroupTherapyEpisode
    ? groupTreatmentPlan.loading
    : chartPlansLoading;
  const plansError = session.isGroupTherapyEpisode ? groupTreatmentPlan.error : null;

  const registerPanelFlush = useCallback((flush: (() => Promise<void>) | null) => {
    panelFlushRef.current = flush;
  }, []);

  const combinedFlush = useCallback(async () => {
    await flushSidebarNotes();
    await panelFlushRef.current?.();
  }, [flushSidebarNotes]);

  useEffect(() => {
    onRegisterFlush?.(combinedFlush);
    return () => onRegisterFlush?.(null);
  }, [combinedFlush, onRegisterFlush]);

  if (session.loading) {
    return (
      <Stack h="100%" mih={320} align="center" justify="center">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading session…
        </Text>
      </Stack>
    );
  }

  if (session.error || !session.client || !session.appointment) {
    return (
      <Stack h="100%" mih={320} align="center" justify="center" px="md">
        <Text size="sm" c="red" ta="center">
          {session.error ?? 'Could not load appointment data for this session.'}
        </Text>
      </Stack>
    );
  }

  return (
    <Box h={layout === 'overlay' ? '100%' : undefined}>
      <ClinicalPanel
        layout={layout}
        client={session.client}
        appointment={session.appointment}
        participants={session.contacts}
        notes={session.notes}
        plans={plans}
        plansLoading={plansLoading}
        plansError={plansError}
        assessments={session.assessments}
        assessmentTrends={session.assessmentTrends}
        protocols={session.protocols}
        patientId={session.patientId}
        episodeId={session.episodeId}
        appointmentId={appointmentId}
        encounterId={session.encounter?.id}
        initialAttendance={session.initialAttendance}
        onSaveAttendance={session.saveAttendance}
        sidebarNotes={session.sidebarNotes}
        onSidebarNotesChange={session.setSidebarNotes}
        sidebarNotesStatus={session.sidebarNotesStatus}
        sidebarNotesError={session.sidebarNotesError}
        onSidebarNotesBlur={() => {
          void session.flushSidebarNotes();
        }}
        onSidebarNotesRetry={() => {
          void session.retrySidebarNotes();
        }}
        onAssessmentSaved={session.refreshChart}
        isGroupTherapyEpisode={session.isGroupTherapyEpisode}
        groupName={session.groupName}
        groupInitials={session.groupInitials}
        groupSubtitle={session.groupSubtitle}
        showPrescribeTab={session.showPrescribeTab}
        treatmentPlanParticipantOptions={participantOptions}
        selectedTreatmentPlanPatientId={selectedTreatmentPlanPatientId}
        onSelectedTreatmentPlanPatientChange={setSelectedTreatmentPlanPatientId}
        clientLocationConfirmations={session.clientLocationConfirmations}
        clientLocationLoading={session.loading}
        clientLocationSaving={session.clientLocationSaving}
        clientLocationSaveError={session.clientLocationSaveError}
        onSaveClientLocation={session.saveClientLocation}
        onClose={onClose}
        onRegisterFlush={registerPanelFlush}
        includeRecordingTab={includeRecordingTab}
        recordingTabContent={recordingTabContent}
      />
    </Box>
  );
}
